/**
 * Onboarding page 1 — « Votre marque » (only at /onboarding/brand/new).
 *
 * URL + working language → Analyze (`POST /brands/discover-url`) → identity (name, aliases,
 * domain, description), activity (secteur, modèle business, envergure), and ONE closed
 * « Options avancées » holding the Cibles to add and « Vos clients » (questions avant achat,
 * objections des prospects, sujets à éviter).
 *
 * « Continuer » creates the brand (`POST /brands`, aliases included so they reach the root
 * marque target), saves + validates the Brand Strategy, keeps the queued Cibles for the
 * wizard to write (pendingTargetsCache), then hands over. « Enregistrer et passer
 * l'assistant » (every tier, owner 2026-11-03) does the same save, also generates +
 * validates the GEO Strategy so the brand is measurable, and exits to Brand Knowledge.
 */
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputBase,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LanguageIcon from '@mui/icons-material/Language';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import type { Brand, BrandStrategy, Envergure, IndustryCode } from '@traaker/shared';
import { BRAND_STRATEGY_LIMITS, INDUSTRY_CODES, industryLabels } from '@traaker/shared';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { pruneEmpty } from '../../components/brand/strategy-sections';
import LanguageSelect from '../../components/common/LanguageSelect';
import { normalizeUiLanguage, type UiLanguage } from '../../i18n/languages';
import {
  AdvancedToggle,
  BUSINESS_MODELS,
  ChoiceList,
  DomainLogo,
  ENVERGURE_STEPS,
  SubCard,
  matchIndustry,
  type ChoiceItem,
} from './onboardingShared';
import { TargetQueueEditor } from './TargetQueueEditor';
import { pendingTargetsCache, type PendingTarget } from './pendingTargets';

const L = BRAND_STRATEGY_LIMITS;
const toItems = (xs?: string[]): ChoiceItem[] => (xs ?? []).map((text) => ({ text, on: true }));
const fromItems = (xs: ChoiceItem[]) => xs.map((x) => x.text);

export function BrandSetupStep({
  onCreated,
  onSkipped,
}: {
  /** The brand exists and its strategy is validated: enter the wizard. */
  onCreated: (brandId: string) => Promise<void>;
  /** « Enregistrer et passer l'assistant »: the brand is saved, leave the wizard. */
  onSkipped: (brand: Brand, queue: PendingTarget[]) => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const { user, setUiLanguage } = useAuth();
  // Prefill from the marketing-funnel brand hint (essai-gratuit form → /signup?brand=… →
  // stash → Onboarding.tsx forwards it as ?brand=). Prefill only: the user still clicks Analyze.
  const [searchParams] = useSearchParams();
  const [url, setUrl] = useState(() => searchParams.get('brand') || '');
  // Brand working language — picked BEFORE analysis so the prefilled fields come back in it.
  // Defaults to the live i18n language (see the 2026-07-31 fix in onboarding.md).
  const [workingLanguage, setWorkingLanguage] = useState<UiLanguage>(normalizeUiLanguage(i18n.language));
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzed, setAnalyzed] = useState(false);
  const [confirmSubPath, setConfirmSubPath] = useState(false);

  const [discovered, setDiscovered] = useState<BrandStrategy>({});
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [aliases, setAliases] = useState<string[]>([]);
  const [aliasInput, setAliasInput] = useState('');
  const [description, setDescription] = useState('');
  const [industry, setIndustry] = useState<IndustryCode | ''>('');
  const [businessModel, setBusinessModel] = useState<'b2b' | 'b2c' | 'both' | ''>('');
  const [envergure, setEnvergure] = useState<Envergure>('mono-country');
  const [questions, setQuestions] = useState<ChoiceItem[]>([]);
  const [objections, setObjections] = useState<ChoiceItem[]>([]);
  const [avoid, setAvoid] = useState<ChoiceItem[]>([]);
  const [queue, setQueue] = useState<PendingTarget[]>([]);
  const [advOpen, setAdvOpen] = useState(false);

  const [saving, setSaving] = useState<'continue' | 'skip' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  // A save that failed AFTER `POST /brands` keeps the brand: a retry must not create a second one.
  const createdRef = useRef<Brand | null>(null);

  const domainFromUrl = (raw: string): string => {
    try {
      const u = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      return new URL(u).hostname.replace(/^www\./, '');
    } catch {
      return raw.trim();
    }
  };

  // A URL scoped to a sub-path (site.com/za/en) audits only that section — warn + offer the root.
  const subPath = (() => {
    const raw = url.trim();
    if (!raw) return '';
    try {
      const u = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      const path = new URL(u).pathname.replace(/\/+$/, '');
      return path && path !== '/' ? path : '';
    } catch {
      return '';
    }
  })();

  const useMainDomain = () => {
    try {
      const raw = url.trim();
      const parsed = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
      setUrl(`${parsed.protocol}//${parsed.host}`);
    } catch {
      /* leave as-is on parse failure */
    }
  };

  const handleAnalyze = () => {
    if (!url.trim()) return;
    if (subPath) {
      setConfirmSubPath(true);
      return;
    }
    void runAnalyze();
  };

  const runAnalyze = async () => {
    if (!url.trim()) return;
    setConfirmSubPath(false);
    setAnalyzing(true);
    setError(null);
    setWarning(null);
    try {
      const res = await api.post<{ success: boolean; name?: string; strategy?: BrandStrategy; message?: string }>(
        '/brands/discover-url',
        { url: url.trim(), language: workingLanguage },
      );
      const s = res.success ? res.strategy || {} : {};
      setDiscovered(s);
      setName(res.success ? res.name || '' : '');
      setDomain(domainFromUrl(url));
      setDescription(s.description || '');
      setAliases(s.aliases || []);
      setIndustry((s.industry && matchIndustry(s.industry as string)) || '');
      setBusinessModel(s.businessModel || '');
      setEnvergure(s.envergure || 'mono-country');
      // Discovery fills customerQuestions; objections + avoidTopics are human-only (brand-discovery.service).
      setQuestions(toItems(s.customerQuestions));
      setObjections(toItems(s.customerObjections));
      setAvoid(toItems(s.avoidTopics));
      if (!res.success) setWarning(res.message || t('onboarding.create.analyzeFailed'));
      setAnalyzed(true);
    } catch (err: any) {
      setError(err.message || t('onboarding.create.analyzeError'));
    } finally {
      setAnalyzing(false);
    }
  };

  const addAlias = () => {
    const v = aliasInput.trim();
    setAliasInput('');
    if (v && !aliases.includes(v) && aliases.length < L.aliases.maxItems) setAliases([...aliases, v]);
  };

  /** Create (once) + save + validate the Brand Strategy. */
  const saveBrand = async (): Promise<Brand> => {
    const brand =
      createdRef.current ??
      (await api.post<Brand>('/brands', { name: name.trim(), url: url.trim() || undefined, aliases }));
    createdRef.current = brand;
    // `targetLanguages` from discovery is never shown here; the prompt step owns languages
    // (a smuggled list would trip the tenant `languages` gauge on this save).
    const { targetLanguages: _languages, ...rest } = discovered;
    const draft: BrandStrategy = {
      ...rest,
      description: description.trim() || undefined,
      aliases,
      ...(industry && { industry }),
      ...(businessModel && { businessModel }),
      envergure,
      customerQuestions: fromItems(questions),
      customerObjections: fromItems(objections),
      avoidTopics: fromItems(avoid),
      // Immutable after onboarding: the language all AI prose for this brand is written in.
      workingLanguage,
    };
    await api.patch(`/brands/${brand.brandId}/strategy`, pruneEmpty(draft));
    await api.post(`/brands/${brand.brandId}/strategy/validate`, {});
    if (workingLanguage !== user?.settings?.uiLanguage) setUiLanguage(workingLanguage).catch(() => {});
    return brand;
  };

  const handleContinue = async () => {
    setSaving('continue');
    setError(null);
    try {
      const brand = await saveBrand();
      pendingTargetsCache.set(brand.brandId, queue);
      await onCreated(brand.brandId);
    } catch (err: any) {
      setError(err.message || t('onboarding.create.createError'));
      setSaving(null);
    }
  };

  const handleSkip = async () => {
    setSaving('skip');
    setError(null);
    try {
      const brand = await saveBrand();
      // The strategy gate needs BOTH layers validated, or every measurement the user adds later is blocked.
      await api.post(`/brands/${brand.brandId}/geo-strategy/generate`, {});
      await api.post(`/brands/${brand.brandId}/geo-strategy/validate`, {});
      await onSkipped(brand, queue);
    } catch (err: any) {
      setError(err.message || t('onboarding.create.createError'));
      setSaving(null);
    }
  };

  const industryLabelMap = industryLabels(i18n.language);
  const advCount = queue.length + objections.length + avoid.length;
  const busy = saving !== null;

  return (
    <Card sx={{ borderRadius: 4 }}>
      {/* URL + language + Analyze */}
      <Box sx={{ px: 4, py: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <FieldLabel>{t('onboarding.create.website')}</FieldLabel>
            <TextField
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && url.trim() && !analyzing) handleAnalyze(); }}
              fullWidth
              size="small"
              autoFocus
              placeholder={t('onboarding.create.urlPlaceholder')}
              disabled={analyzing || busy}
            />
          </Box>
          <Box sx={{ width: 170 }}>
            <FieldLabel tooltip={t('onboarding.create.workingLanguageHint')}>{t('onboarding.setup.language')}</FieldLabel>
            <LanguageSelect value={workingLanguage} onChange={setWorkingLanguage} disabled={analyzing || busy} fullWidth />
          </Box>
          <Button
            variant="contained"
            onClick={handleAnalyze}
            disabled={!url.trim() || analyzing || busy}
            startIcon={analyzing ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon fontSize="small" />}
            sx={{ whiteSpace: 'nowrap', px: 3, height: 40 }}
          >
            {analyzing ? t('onboarding.create.analyzing') : analyzed ? t('onboarding.setup.reanalyze') : t('onboarding.create.analyze')}
          </Button>
        </Box>
        {subPath && (
          <Alert
            severity="warning"
            sx={{ mt: 1.5 }}
            action={<Button color="inherit" size="small" onClick={useMainDomain}>{t('onboarding.create.useMainDomain')}</Button>}
          >
            {t('onboarding.create.subPathWarning', { path: subPath })}
          </Alert>
        )}
      </Box>

      <Dialog open={confirmSubPath} onClose={() => setConfirmSubPath(false)}>
        <DialogTitle>{t('onboarding.create.subPathConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('onboarding.create.subPathConfirmBody', { path: subPath })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { useMainDomain(); setConfirmSubPath(false); }}>{t('onboarding.create.useMainDomain')}</Button>
          <Button variant="contained" onClick={() => void runAnalyze()}>{t('onboarding.create.subPathConfirmProceed')}</Button>
        </DialogActions>
      </Dialog>

      {analyzing && <AnalyzeSkeleton />}

      {analyzed && !analyzing && (
        <>
          {/* Identity */}
          <Section>
            {warning && <Alert severity="info" sx={{ mb: 2 }}>{warning}</Alert>}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <DomainLogo domain={domain} size={52} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <InputBase
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('onboarding.create.brandName')}
                  inputProps={{ 'aria-label': t('onboarding.create.brandName'), maxLength: 120 }}
                  sx={{
                    fontSize: 20, fontWeight: 700, width: '100%', px: 1.25, py: 0.5, ml: -1.25, borderRadius: 2.5,
                    border: '1px solid transparent', '&:hover': { borderColor: 'divider' }, '&.Mui-focused': { borderColor: 'primary.main' },
                  }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mt: 0.75 }}>
                  <Chip size="small" icon={<LanguageIcon sx={{ fontSize: 15 }} />} label={domain} sx={{ bgcolor: 'action.hover' }} />
                  {aliases.map((a, i) => (
                    <Chip key={`${a}-${i}`} size="small" variant="outlined" label={a} onDelete={() => setAliases(aliases.filter((_, j) => j !== i))} />
                  ))}
                  {aliases.length < L.aliases.maxItems && (
                    <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 999, px: 1.25, height: 24, display: 'flex', alignItems: 'center' }}>
                      <InputBase
                        value={aliasInput}
                        onChange={(e) => setAliasInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAlias(); } }}
                        onBlur={addAlias}
                        placeholder={t('onboarding.setup.aliasPlaceholder')}
                        inputProps={{ maxLength: L.aliases.maxCharsPerItem, 'aria-label': t('onboarding.create.aliases') }}
                        sx={{ fontSize: 12.5, width: 110 }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            <Box sx={{ mt: 2 }}>
              <FieldLabel>{t('onboarding.create.description')}</FieldLabel>
              <TextField
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                size="small"
                inputProps={{ maxLength: L.description }}
              />
            </Box>
          </Section>

          {/* Activity */}
          <Section>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <FieldLabel>{t('onboarding.setup.industry')}</FieldLabel>
                <Select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value as IndustryCode)}
                  displayEmpty
                  fullWidth
                  size="small"
                  renderValue={(v) => (v ? industryLabelMap[v as IndustryCode] : <Box component="span" sx={{ color: 'text.disabled' }}>{t('onboarding.setup.industryPlaceholder')}</Box>)}
                >
                  {INDUSTRY_CODES.map((c) => <MenuItem key={c} value={c}>{industryLabelMap[c]}</MenuItem>)}
                </Select>
              </Box>
              <Box>
                <FieldLabel>{t('onboarding.setup.businessModel')}</FieldLabel>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={businessModel}
                  onChange={(_, v) => v && setBusinessModel(v)}
                  sx={{ height: 40 }}
                >
                  {BUSINESS_MODELS.map((m) => (
                    <ToggleButton key={m.value} value={m.value} sx={{ px: 2, fontWeight: 600 }}>{t(m.labelKey)}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ width: 190 }}>
                <FieldLabel tooltip={t('onboarding.knowledge.activity.geoScopeTooltip')}>{t('onboarding.setup.envergure')}</FieldLabel>
                <Select value={envergure} onChange={(e) => setEnvergure(e.target.value as Envergure)} fullWidth size="small">
                  {ENVERGURE_STEPS.map((s) => <MenuItem key={s.value} value={s.value}>{t(s.labelKey)}</MenuItem>)}
                </Select>
              </Box>
            </Box>
          </Section>

          {/* Options avancées — closed by default (owner 2026-11-03: hide the complexity) */}
          <AdvancedToggle open={advOpen} onToggle={() => setAdvOpen((o) => !o)} count={advCount}>
            <SubCard icon={<TrackChangesIcon sx={{ fontSize: 17 }} />} title={t('onboarding.setup.targets.title')}>
              <TargetQueueEditor domain={domain} queue={queue} onChange={setQueue} />
            </SubCard>
            <SubCard icon={<ForumOutlinedIcon sx={{ fontSize: 17 }} />} title={t('onboarding.setup.clients.title')}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                <ListColumn title={t('onboarding.setup.clients.questions')} items={questions} onChange={setQuestions} limit={L.customerQuestions} />
                <ListColumn title={t('onboarding.setup.clients.objections')} items={objections} onChange={setObjections} limit={L.customerObjections} />
                <ListColumn title={t('onboarding.setup.clients.avoid')} items={avoid} onChange={setAvoid} limit={L.avoidTopics} />
              </Box>
            </SubCard>
          </AdvancedToggle>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 4, py: 2.25, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button variant="text" onClick={handleSkip} disabled={!name.trim() || busy}>
              {saving === 'skip' ? <CircularProgress size={18} /> : t('onboarding.setup.saveAndSkip')}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" size="large" onClick={handleContinue} disabled={!name.trim() || busy} sx={{ px: 4, borderRadius: 3 }}>
              {saving === 'continue' ? <CircularProgress size={20} color="inherit" /> : t('onboarding.common.continue')}
            </Button>
          </Box>
        </>
      )}
    </Card>
  );
}

function Section({ children }: { children: React.ReactNode }) {
  return <Box sx={{ px: 4, py: 2.75, borderTop: '1px solid', borderColor: 'divider' }}>{children}</Box>;
}

function FieldLabel({ children, tooltip }: { children: React.ReactNode; tooltip?: string }) {
  const label = <Box sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>{children}</Box>;
  return tooltip ? <Tooltip title={tooltip} arrow placement="top-start">{label}</Tooltip> : label;
}

function ListColumn({
  title,
  items,
  onChange,
  limit,
}: {
  title: string;
  items: ChoiceItem[];
  onChange: (items: ChoiceItem[]) => void;
  limit: { maxItems: number; maxCharsPerItem: number };
}) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>
        {title}
        <Box component="span" sx={{ ml: 'auto', fontWeight: 500, color: 'text.disabled' }}>{items.length}</Box>
      </Box>
      <ChoiceList items={items} onChange={onChange} maxChars={limit.maxCharsPerItem} maxItems={limit.maxItems} />
    </Box>
  );
}

// Grey placeholder bars shown while /discover-url runs.
function AnalyzeSkeleton() {
  const bar = (w: string | number, h = 14) => <Box sx={{ width: w, height: h, borderRadius: 1, bgcolor: 'action.hover' }} />;
  return (
    <Section>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2.5 }}>
        <Box sx={{ width: 52, height: 52, borderRadius: 2.5, bgcolor: 'action.hover' }} />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>{bar('40%', 20)}{bar('25%')}</Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>{bar('100%', 60)}{bar('70%')}</Box>
    </Section>
  );
}
